<template>
  <v-row>
    <v-col>
      <stepper
        ref="stepperWizard"
        editable
        :steps="computedSteps"
        :stepper-props="{
          altLabels: true,
        }"
        :stepper-classes="['elevation-0']"
        :stepper-header-classes="['elevation-0', 'pl-5']"
        :stepper-buttons-classes="['pl-8']"
      >
        <template v-for="step in computedSteps.slice(0, -1)" #[step.step]="">
          <sidebar-row :key="step.step" style="padding-right: 0 !important">
            <slot :name="`stepPrepend-${step.step}`"></slot>
            <sidebar-form
              :is-valid.sync="stepsValidations[`step${step.step}`]"
              v-model="params"
              :ref="`step${step.step}`"
              :inputs="inputs.filter(input => input.step === step.step)"
              :hasIconColumn="false"
              class="wizard-form"
            >
              <template v-for="input in inputs.filter(input => input.step === step.step)" #[input.name]="data">
                <data-input
                  v-bind="{ ...getInputProps(data) }"
                  :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                  v-model="params[data.name]"
                  @change="inputChangeHandler(data, step)"
                  v-on="{ ...additionalDataInputListeners(data, step) }"
                  :key="input.name"
                />
              </template>
            </sidebar-form>
            <slot :name="`stepAppend-${step.step}`"></slot>
          </sidebar-row>
        </template>
        <template #[computedSteps.at(-1).step]="">
          <div class="d-flex pt-2" :class="[resultBelow ? 'flex-column-reverse' : 'flex-column']">
            <sidebar-row v-if="isAnalysisDone">
              <slot name="analysisDoneCallback"></slot>
            </sidebar-row>
            <sidebar-row class="pb-2">
              <slot name="stepPrepend-last"></slot>
              <v-row class="align-baseline">
                <v-col cols="auto" class="d-flex py-0 py-5">
                  <base-button-subheader
                    v-if="isAnalysisDone"
                    translation-path="sidebar.stepsWizardResetAnalysis"
                    @click="isResetDialogVisible = true"
                    :disabled="isAnalysisLoading"
                    cancel
                  />
                  <base-button-subheader
                    :translation-path="
                      isAnalysisDone ? 'sidebar.stepsWizardTurnAnalysisAgain' : 'sidebar.stepsWizardTurnOnAnalysis'
                    "
                    @click="wizardActionHandler"
                    :disabled="!allDataValid"
                    :loading="isAnalysisLoading"
                  />
                </v-col>
              </v-row>
            </sidebar-row>
          </div>
        </template>
      </stepper>
    </v-col>
    <main-dialog
      :is-visible.sync="isResetDialogVisible"
      :title="$i18n.t('dialog.title.resetStepsWizardConfig')"
      :body="$i18n.t('dialog.body.resetStepsWizardConfigConfirmation')"
      @agree="resetAnalysis"
      agree-text="dialog.reset"
    />
  </v-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import SidebarForm from '@/components/SidebarForm';

import { get, call } from 'vuex-pathify';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'StepsWizard',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    Stepper: () => import('@/components/Stepper'),
    DataInput: () => import('@/components/DataInput'),
    MainDialog: () => import('@/components/MainDialog'),
    SidebarRow,
    SidebarForm,
  },
  props: {
    resultBelow: {
      type: Boolean,
      default: false,
    },
    inputs: {
      type: Array,
      required: true,
      default: () => [],
    },
    steps: {
      type: Array,
      required: true,
      default: () => [],
    },
    paramsProxy: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    additionalDataInputBinds: {
      type: Function,
      default: () => {},
    },
    isAnalysisDoneProxy: {
      type: Boolean,
      required: true,
      default: false,
    },
    isAnalysisLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    wizardActionHandler: {
      type: Function,
      default: () => {},
    },
    additionalDataInputListeners: {
      type: Function,
      default: () => {},
    },
    customResetHandler: {
      type: Function,
      default: () => {},
    },
    additionalIsDataValid: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isResetDialogVisible: false,
    stepsValidations: {},
    itemsList: {},
    inputLoading: {},
  }),
  computed: {
    dataSources: get('layers/metadata'),
    projectElements: get('layers/project@layers'),
    layers: get('layers/layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    params: {
      get() {
        return this.paramsProxy;
      },
      set(nV) {
        this.$emit('update:paramsProxy', nV);
      },
    },
    isAnalysisDone: {
      get() {
        return this.isAnalysisDoneProxy;
      },
      set(nV) {
        this.$emit('update:isAnalysisDoneProxy', nV);
      },
    },
    computedSteps() {
      return this.steps.map(step => {
        return {
          ...step,
          backDisabled: this.isAnalysisLoading || step.backDisabled,
          nextDisabled: !this.stepsValidations[`step${step.step}`] || step.nextDisabled,
        };
      });
    },
    allStepsValid() {
      return Object.values(this.stepsValidations).every(step => !!step);
    },
    allDataValid() {
      return this.allStepsValid && this.additionalIsDataValid;
    },
  },
  methods: {
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    resetAnalysis() {
      this.isResetDialogVisible = false;
      this.isAnalysisDone = false;
      const paramsBeforeClean = JSON.parse(JSON.stringify(this.params));
      Object.keys(this.stepsValidations).forEach(step => {
        this.$refs[step][0]?.$refs.form.reset();
      });
      this.$refs['stepperWizard']?.reset();
      this.customResetHandler(paramsBeforeClean);
    },
    inputChangeHandler(data, step) {
      if (data.isLayer) {
        const paramsCopy = JSON.parse(JSON.stringify(this.params));
        const inputsToSkip = this.inputs
          .filter(input => input.step === step.step && input.skipFormReset)
          ?.map(input => input.name);
        const layer = this.params[data.name];
        if (this.$refs[`step${step.step}`]) this.$refs[`step${step.step}`][0]?.$refs.form.reset();
        inputsToSkip.forEach(inputName => (this.params[inputName] = paramsCopy[inputName]));
        this.params[data.name] = layer;
      }
      if (data.connectedValue) {
        Array.isArray(data.connectedValue)
          ? data.connectedValue.forEach(value => (this.params[value] = null))
          : (this.params[data.connectedValue] = null);
      }
    },
    async handleAttributeChange(nV, input) {
      const currentLayer = this.layers[this.params[input.connectedLayer]];
      if (currentLayer && nV) {
        this.inputLoading[input.name] = true;
        try {
          const uniqueValues = await this.getDataSourceAttributeUniqueValues({
            dataSourceName: currentLayer.data_source_name,
            attributeName: nV,
            params: {
              features_filter: currentLayer.filter_expression,
            },
          });
          this.itemsList[input.name] = Object.freeze(Object.keys(uniqueValues.values));
        } catch (e) {
          console.log('error', e);
        } finally {
          this.inputLoading[input.name] = false;
          this.$forceUpdate();
        }
      }
    },
    handleLayerChange(nV, inputName) {
      const currentLayer = this.layers[nV];
      if (currentLayer) {
        const dataSourceAttributes = this.dataSources[currentLayer?.data_source_name]?.attributes_schema.attributes;
        const schemaForm = this.getForm(currentLayer?.form_schema.elements, dataSourceAttributes);
        this.itemsList[inputName] = this.$_flattenStructure(schemaForm, 'elements');
      } else {
        this.itemsList[inputName] = [];
      }
      this.$forceUpdate();
    },
    getInputProps(data) {
      return {
        ...data,
        dense: true,
        ...(data.connectedAttribute && {
          items: this.itemsList[data.connectedAttribute],
          loading: this.inputLoading[data.connectedAttribute],
          disabled: data.disabled || this.inputLoading[data.connectedAttribute],
        }),
        ...(data.connectedLayer && {
          items: data.allowedAttributeTypes
            ? this.itemsList[data.connectedLayer]?.filter(attr =>
                [...data.allowedAttributeTypes].includes(attr.dataType.name)
              )
            : this.itemsList[data.connectedLayer],
        }),
        ...this.additionalDataInputBinds(data),
      };
    },
    attachListeners() {
      this.inputs.forEach(input => {
        if (input.isLayer) {
          this.$watch(`params.${input.name}`, nV => {
            if (nV) this.handleLayerChange(nV, input.name, input.step);
          });
        }
        if (input.connectedValue && !input.isNotAttribute) {
          this.$watch(`params.${input.name}`, nV => {
            if (nV) this.handleAttributeChange(nV, input);
          });
        }
      });
    },
  },
  mounted() {
    this.attachListeners();
  },
};
</script>

<style lang="scss" scoped>
::v-deep .wizard-form .row {
  padding-right: 0 !important;
}
::v-deep .v-select__selections {
  flex-wrap: wrap !important;
}
</style>
